import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaEdit, FaTrash, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import MemberForm from './MemberForm';
import Table from '../common/Table';
import IconButton from '../common/IconButton';
import Button from '../common/Button';
import useApi from '../../hooks/useApi';
import { toast } from 'react-toastify';
import 'animate.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// MemberCard component for mobile view
const MemberCard = React.memo(({ member, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  const isSelfRelation = member.relation === 'SELF';

  return (
    <div className="member-card animate__animated animate__fadeInUp">
      <div className="member-card-header" onClick={toggleExpand}>
        <div>
          <h6 className="mb-0 text-dark">{member.name}</h6>
          {/* <small className="text-muted">{member.ownershipType || 'N/A'}</small> */}
        </div>
        <div className="d-flex align-items-center gap-2">
          {isExpanded ? (
            <FaChevronUp size={14} className="text-muted" />
          ) : (
            <FaChevronDown size={14} className="text-muted" />
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="member-card-body">
          <div className="mb-2">
            <strong className="text-dark">Gender:</strong>{' '}
            <span className="text-muted">{member.gender || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Blood Group:</strong>{' '}
            <span className="text-muted">{member.bloodGroup || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Age:</strong>{' '}
            <span className="text-muted">{member.age || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Birthdate:</strong>{' '}
            <span className="text-muted">{member.birthdate || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Mobile No:</strong>{' '}
            <span className="text-muted">{member.mobileNo || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Relation:</strong>{' '}
            <span className="text-muted">{member.relation || '-'}</span>
          </div>
          <div className="mt-3 d-flex gap-2">
            <IconButton
              icon={<FaEdit />}
              tooltip="Edit"
              variant="warning"
              onClick={() => onEdit(member)}
              className="flex-fill"
            />
            <IconButton
              icon={<FaTrash />}
              tooltip={isSelfRelation ? 'Cannot delete default member' : 'Delete'}
              variant="danger"
              onClick={() => !isSelfRelation && onDelete(member.id)}
              className="flex-fill"
              disabled={isSelfRelation}
            />
          </div>
        </div>
      )}
    </div>
  );
});

function MemberList({ members, setMembers }) {
  const [editingMember, setEditingMember] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const { delete: deleteMember } = useApi();

  const handleDelete = async (id) => {
    try {
      await deleteMember(`/api/members/${id}`);
      setMembers(members.filter((member) => member.id !== id));
      toast.success('Member deleted.');
    } catch (err) {
      toast.error('Failed to delete member.');
    }
  };

  const handleSuccess = (response) => {
    if (response) {
      if (editingMember) {
        setMembers((prev) =>
          prev.map((m) => (m.id === response.id ? response : m))
        );
      } else {
        setMembers((prev) => [...prev, response]);
      }
    }
    setEditingMember(null);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingMember(null);
    setIsAdding(false);
  };

  const columns = [
    'Name',
    'Gender',
    'Blood Group',
    'Birthdate',
    'Age',
    // 'Ownership',
    'Mobile No',
    'Relation',
    'Actions',
  ];

  const renderRow = (member) => {
    const isSelfRelation = member.relation === 'SELF';
    return (
      <tr key={member.id}>
        <td>{member.name}</td>
        <td>{member.gender || '-'}</td>
        <td>{member.bloodGroup || '-'}</td>
        <td>{member.birthdate || '-'}</td>
        <td>{member.age || '-'}</td>
        {/* <td>{member.ownershipType || '-'}</td> */}
        <td>{member.mobileNo || '-'}</td>
        <td>{member.relation || '-'}</td>
        <td>
          <IconButton
            icon={<FaEdit />}
            tooltip="Edit"
            variant="warning"
            onClick={() => {
              setIsAdding(false);
              setEditingMember(member);
            }}
            className="me-2"
          />
          <IconButton
            icon={<FaTrash />}
            tooltip={isSelfRelation ? 'Cannot delete default member' : 'Delete'}
            variant="danger"
            onClick={() => !isSelfRelation && handleDelete(member.id)}
            disabled={isSelfRelation}
          />
        </td>
      </tr>
    );
  };

  return (
    <div className="container py-4 animate__animated animate__fadeIn">
      <div className="card shadow border-0">
        <div className="card-body">
          <h5 className="card-title fs-5 mb-3 text-dark">My Members</h5>
          <div className="mb-3">
            <Button
              variant="primary"
              onClick={() => {
                setEditingMember(null);
                setIsAdding(true);
              }}
              disabled={isAdding || editingMember !== null}
            >
              <FaPlus /> Add Member
            </Button>
          </div>
          {(isAdding || editingMember !== null) && (
            <div className="mb-4">
              <MemberForm
                member={editingMember}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
              />
            </div>
          )}
          {members.length === 0 ? (
            <div className="alert alert-info">No members found.</div>
          ) : (
            <>
              <style>
                {`
                  .member-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                    transition: transform 0.2s, box-shadow 0.2s;
                    background-color: #fff;
                    overflow: hidden;
                  }
                  .member-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
                  }
                  .member-card-header {
                    background-color: #f8f9fa;
                    padding: 12px 16px;
                    border-bottom: none;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                  }
                  .member-card-body {
                    padding: 16px;
                    background-color: #fff;
                  }
                  .member-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                  }
                  .member-card {
                    flex: 1 1 300px;
                    max-width: 300px;
                  }
                  .table-responsive {
                    overflow-x: auto;
                  }
                  @media (max-width: 576px) {
                    .desktop-table {
                      display: none;
                    }
                    .mobile-grid {
                      display: flex;
                    }
                    .member-card {
                      flex: 1 1 100%;
                      max-width: 100%;
                    }
                  }
                  @media (min-width: 577px) {
                    .desktop-table {
                      display: block;
                    }
                    .mobile-grid {
                      display: none;
                    }
                  }
                `}
              </style>
              <div className="desktop-table">
                <Table columns={columns} data={members} renderRow={renderRow} />
              </div>
              <div className="mobile-grid">
                <div className="member-grid">
                  {members.map((member) => (
                    <MemberCard
                      key={member.id}
                      member={member}
                      onEdit={() => {
                        setIsAdding(true);
                        setEditingMember(member);
                      }}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

MemberList.propTypes = {
  members: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      gender: PropTypes.any,
      bloodGroup: PropTypes.string,
      age: PropTypes.number,
      birthdate: PropTypes.string,
      // ownershipType: PropTypes.string,
      mobileNo: PropTypes.string,
      relation: PropTypes.string,
    })
  ).isRequired,
  setMembers: PropTypes.func.isRequired,
};

MemberCard.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    gender: PropTypes.string,
    bloodGroup: PropTypes.string,
    age: PropTypes.number,
    birthdate: PropTypes.string,
    // ownershipType: PropTypes.string,
    mobileNo: PropTypes.string,
    relation: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default MemberList;